import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();

    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);

    if (this.hasAttribute('data-interactive')) {
      this.refs.wrapper.addEventListener('pointerdown', this.#handlePointerDown);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);

    if (this.hasAttribute('data-interactive')) {
      this.refs.wrapper.removeEventListener('pointerdown', this.#handlePointerDown);
    }
  }

  /**
   * Advances the marquee backward by roughly one item. Wired up via
   * `on:click="/previous"` on a `slideshow-arrow` button.
   */
  previous() {
    this.#nudge(-1);
  }

  /**
   * Advances the marquee forward by roughly one item. Wired up via
   * `on:click="/next"` on a `slideshow-arrow` button.
   */
  next() {
    this.#nudge(1);
  }

  /**
   * Manually shifts the marquee's animation position forward or backward by
   * one full visible screen's worth of products (like paging a carousel),
   * then lets it continue playing automatically from there.
   * @param {1 | -1} direction
   */
  #nudge(direction) {
    const { wrapper } = this.refs;
    const animation = wrapper.getAnimations()[0];
    if (!animation) return;

    const durationMs = Number(animation.effect?.getTiming().duration) || 0;
    const rowTimeDelta = this.#getRowTimeDelta(animation);
    if (!durationMs || !rowTimeDelta) return;

    const currentTime = Number(animation.currentTime) || 0;
    let newTime = (currentTime + direction * rowTimeDelta) % durationMs;
    if (newTime < 0) newTime += durationMs;

    animation.pause();
    animation.currentTime = newTime;
    animation.play();
  }

  /**
   * Converts one full visible row's width into the equivalent duration of
   * animation time, so a "row" can be expressed as a currentTime offset.
   *
   * The row width is measured from the actual on-screen spacing between
   * product cards (not just the container's bounding width), then rounded
   * to a whole number of cards, so a "row" always lands on a card boundary
   * instead of an arbitrary fractional offset.
   * @param {Animation} animation
   * @returns {number}
   */
  #getRowTimeDelta(animation) {
    const { wrapper, marqueeItems } = this.refs;
    const items = marqueeItems[0]?.children;
    if (!items?.length) return 0;

    const totalDistance = wrapper.scrollWidth / 2;
    const durationMs = Number(animation.effect?.getTiming().duration) || 0;
    if (!totalDistance || !durationMs) return 0;

    const firstItem = items[0];
    if (!firstItem) return 0;

    const itemStride =
      items.length > 1
        ? items[1].getBoundingClientRect().left - firstItem.getBoundingClientRect().left
        : firstItem.getBoundingClientRect().width;
    if (!itemStride) return 0;

    const visibleWidth = this.getBoundingClientRect().width || itemStride;
    const itemsPerRow = Math.max(1, Math.round(visibleWidth / itemStride));
    const rowWidth = itemsPerRow * itemStride;

    return (rowWidth / totalDistance) * durationMs;
  }

  /**
   * Lets the shopper manually drag/swipe the marquee forward or backward.
   * The underlying CSS animation is paused during the drag and resumed
   * (continuing automatically) once the pointer is released.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const { wrapper } = this.refs;
    const animation = wrapper.getAnimations()[0];
    if (!animation) return;

    const totalDistance = wrapper.scrollWidth / 2;
    const durationMs = Number(animation.effect?.getTiming().duration) || 0;
    if (!totalDistance || !durationMs) return;

    const startX = event.clientX;
    const startTime = Number(animation.currentTime) || 0;

    animation.pause();

    let moved = false;
    let lastDeltaX = 0;
    const controller = new AbortController();
    const { signal } = controller;

    const onMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      lastDeltaX = deltaX;

      if (!moved && Math.abs(deltaX) > 3) {
        moved = true;
        this.setAttribute('dragging', '');
      }

      if (!moved) return;

      const timeDelta = (-deltaX / totalDistance) * durationMs;
      let newTime = (startTime + timeDelta) % durationMs;
      if (newTime < 0) newTime += durationMs;

      animation.currentTime = newTime;
    };

    const onUp = () => {
      controller.abort();
      this.removeAttribute('dragging');

      if (moved) {
        document.addEventListener('click', preventClickAfterDrag, { once: true, capture: true });

        // Snap to a full row jump in the swiped direction, by calling the
        // exact same method the arrow buttons use, instead of leaving it
        // wherever the free drag landed.
        if (lastDeltaX < 0) {
          this.next();
        } else {
          this.previous();
        }
      } else {
        animation.play();
      }
    };

    document.addEventListener('pointermove', onMove, { signal });
    document.addEventListener('pointerup', onUp, { signal, once: true });
    document.addEventListener('pointercancel', onUp, { signal, once: true });
  };

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const { newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    const currentNumberOfCopies = marqueeItems.length;
    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

/**
 * Swallows the click that would otherwise fire on a marquee item right after
 * the shopper finishes a drag/swipe, so dragging doesn't accidentally
 * navigate to a product.
 * @param {MouseEvent} event
 */
function preventClickAfterDrag(event) {
  event.preventDefault();
  event.stopPropagation();
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
